import { Link } from "react-router-dom";
import { Check, ArrowRight, ListChecks, Sparkles } from "lucide-react";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/LanguageProvider";
import { useRegisterAlternates } from "../components/layout/Layout";
import { entityPath, pagePath } from "../config/pages";
import { getTreatment, TREATMENTS } from "../data/treatments";
import { LOCATIONS } from "../data/locations";
import { PageHero, CTABand, SectionHeading } from "../components/Sections";
import { Reveal, RevealStagger, RevealItem } from "../components/motion/Reveal";
import { TreatmentIcon } from "../components/iconMap";
import { LocationCard } from "../components/Cards";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import ConsultationForm from "../components/ConsultationForm";
import Technology from "../components/Technology";
import { webPageSchema, breadcrumbSchema, faqSchema } from "../lib/schema";

export default function TreatmentPage({ treatmentKey }) {
  const { t, lang, p, e } = useI18n();
  const tr = getTreatment(treatmentKey);
  const alternates = {
    en: entityPath(null, tr.slug, "en"),
    fr: entityPath(null, tr.slug, "fr"),
    de: entityPath(null, tr.slug, "de"),
  };
  useRegisterAlternates(alternates);

  const name = tr.name[lang] || tr.name.en;
  const path = entityPath(null, tr.slug, lang);
  const crumbs = [
    { name: t.nav.treatments, to: p("dentalTreatment") },
    { name, to: path },
  ];
  const faqList = tr.faqs.map((f) => ({ q: f.q[lang] || f.q.en, a: f.a[lang] || f.a.en }));
  const related = (tr.related || []).map(getTreatment).filter(Boolean);

  return (
    <>
      <Seo
        title={tr.metaTitle[lang] || tr.metaTitle.en}
        description={tr.metaDesc[lang] || tr.metaDesc.en}
        path={path}
        alternates={alternates}
        lang={lang}
        image={tr.image}
        jsonLd={[
          webPageSchema({ name, description: tr.metaDesc[lang] || tr.metaDesc.en, path }),
          breadcrumbSchema([{ name: t.common.home, path: p("home") }, ...crumbs.map((c) => ({ name: c.name, path: c.to }))]),
          faqSchema(faqList),
        ]}
      />

      <PageHero
        eyebrow={t.treatmentsSection.eyebrow}
        title={name}
        intro={tr.tagline[lang] || tr.tagline.en}
        crumbs={crumbs}
        image={{ src: tr.image, alt: `${name} — Dentel Vietnam` }}
      />

      <div className="container-dv py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Main content */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="prose-dv">
                {(tr.overview[lang] || tr.overview.en).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <div className="mt-10 grid gap-6 sm:grid-cols-2">
                <div className="rounded-3xl border border-navy/8 bg-white p-6 shadow-soft">
                  <ListChecks className="h-6 w-6 text-dental" />
                  <h2 className="mt-3 text-xl text-navy">{t.howItWorks.eyebrow}</h2>
                  <ul className="mt-4 space-y-2.5">
                    {(tr.involves[lang] || tr.involves.en).map((it, i) => (
                      <li key={i} className="flex gap-2.5 text-[15px] text-navy/75">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-turquoise" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-3xl border border-navy/8 bg-white p-6 shadow-soft">
                  <Sparkles className="h-6 w-6 text-turquoise" />
                  <h2 className="mt-3 text-xl text-navy">{t.cta.learnMore}</h2>
                  <ul className="mt-4 space-y-2.5">
                    {(tr.benefits[lang] || tr.benefits.en).map((b, i) => (
                      <li key={i} className="flex gap-2.5 text-[15px] text-navy/75">
                        <Check className="mt-0.5 h-4.5 w-4.5 shrink-0 text-turquoise" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            {/* Rich SEO sections (optional, data-driven) */}
            {tr.sections && tr.sections.length > 0 && (
              <div className="mt-10 space-y-8">
                {tr.sections.map((s, i) => (
                  <Reveal key={i} delay={0.03 * i}>
                    <section>
                      <h2 className="text-2xl sm:text-3xl text-navy scroll-mt-28">{s.h[lang] || s.h.en}</h2>
                      {s.body && (
                        <div className="prose-dv mt-3">
                          {(s.body[lang] || s.body.en).map((para, j) => <p key={j}>{para}</p>)}
                        </div>
                      )}
                      {s.list && (
                        <ul className="mt-3 space-y-2.5">
                          {(s.list[lang] || s.list.en).map((li, j) => (
                            <li key={j} className="flex gap-3 text-navy/75 text-base sm:text-lg leading-relaxed">
                              <Check className="mt-1 h-5 w-5 shrink-0 text-turquoise" />
                              <span>{li}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  </Reveal>
                ))}
              </div>
            )}

            {/* Price note */}
            <Reveal delay={0.05}>
              <div className="mt-8 rounded-2xl border border-dental/15 bg-dental/5 p-5">
                <p className="text-sm font-semibold text-dental">{t.common.pricePlaceholder}</p>
                <p className="mt-1 text-sm leading-relaxed text-navy/65">{t.common.indicativePrice}</p>
              </div>
            </Reveal>

            {/* FAQs */}
            {faqList.length > 0 && (
              <Reveal delay={0.05}>
                <h2 className="mt-12 text-2xl sm:text-3xl text-navy">{t.faqSection.title}</h2>
                <Accordion type="single" collapsible className="mt-6 space-y-3">
                  {faqList.map((item, i) => (
                    <AccordionItem key={i} value={`f-${i}`} className="rounded-2xl border border-navy/10 bg-white px-5 shadow-soft data-[state=open]:border-dental/30">
                      <AccordionTrigger data-testid={`treatment-faq-${i}`} className="py-5 text-left text-base font-semibold text-navy hover:no-underline">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 text-[15px] leading-relaxed text-navy/70">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}

            {/* Disclaimer */}
            <div className="mt-10 rounded-2xl border border-navy/10 bg-mist p-6 text-sm leading-relaxed text-navy/70">
              <strong className="text-navy">{t.common.disclaimerTitle}.</strong> {t.common.disclaimer}
            </div>
          </div>

          {/* Sidebar form */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <h2 className="mb-4 text-xl text-navy">{t.form.title}</h2>
              <ConsultationForm defaultTreatment={tr.key} />
            </div>
          </aside>
        </div>
      </div>

      {/* Technology (implants only) */}
      {tr.key === "dental-implants" && (
        <div className="bg-mist">
          <Technology />
        </div>
      )}

      {/* Related treatments */}
      {related.length > 0 && (
        <section className="bg-mist py-16 lg:py-20" data-testid="related-treatments">
          <div className="container-dv">
            <SectionHeading eyebrow={t.common.relatedTreatments} title={t.common.relatedTreatments} />
            <RevealStagger className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((rt) => (
                <RevealItem key={rt.key}>
                  <Link
                    to={e(rt.slug)}
                    data-testid={`related-treatment-${rt.key}`}
                    className="group flex items-center gap-4 rounded-3xl border border-navy/8 bg-white p-5 shadow-soft transition-all hover:shadow-lift hover:-translate-y-1"
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-dental/8 text-dental">
                      <TreatmentIcon name={rt.icon} className="h-6 w-6" />
                    </span>
                    <span className="flex-1">
                      <span className="block font-semibold text-navy">{rt.name[lang] || rt.name.en}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-navy/30 transition-transform group-hover:translate-x-1" />
                  </Link>
                </RevealItem>
              ))}
            </RevealStagger>
          </div>
        </section>
      )}

      {/* Related cities */}
      <section className="container-dv py-16 lg:py-20">
        <SectionHeading eyebrow={t.common.relatedCities} title={t.locationsSection.title} />
        <RevealStagger className="mt-8 grid gap-6 md:grid-cols-3">
          {LOCATIONS.map((lo) => (
            <RevealItem key={lo.key}>
              <LocationCard location={lo} />
            </RevealItem>
          ))}
        </RevealStagger>
      </section>

      <CTABand />
    </>
  );
}
